import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import { Heading } from '../StyledComponents';
import { Button } from '../../../../shared/FormStyles';
import { deleteUser } from '../../data';
import RadioInput from '../../../../shared/RadioInput';
import { REMOVE_FROM_BOARD, DESTROY_USER } from '../../constants';
import {
  CLIENT_ADMIN,
  ORG_ADMIN,
  RESTRICTED_SITE_ADMIN,
  SITE_ADMIN,
} from '../../../../lib/utils/constants/UserTypes';

const allowedToDestroyUserRoles = [
  ORG_ADMIN,
  SITE_ADMIN,
  CLIENT_ADMIN,
  RESTRICTED_SITE_ADMIN,
];

export default function DeleteUser({ userId, userName }) {
  const { t } = useTranslation(['users', 'shared']);
  const queryClient = useQueryClient();
  const [deletionType, setDeletionType] = useState('removeFromBoard');

  const { mutate } = useMutation(deleteUser, {
    onSuccess: (res, { userId }) => {
      queryClient.setQueriesData('getUsers', data => {
        const { users } = data;
        const updatedUsers = users.map(user => {
          if (user.id === userId) {
            return { ...user, markedForDeletion: true };
          }

          return user;
        });
        return { ...data, users: updatedUsers };
      });

      Swal.close();
    },
  });

  const { userType: currentUserType, boardName } = useSelector(state => {
    const { userType } = state.currentUser;

    const { name: boardName } = state.currentBoard;

    return { boardName, userType };
  });

  const radioOptions = [
    { value: REMOVE_FROM_BOARD, label: boardName },
    { value: DESTROY_USER, label: 'All Boards' },
  ];

  return (
    <Wrapper>
      <Heading as="h2">{t('users:deleteUser.heading')}</Heading>
      {allowedToDestroyUserRoles.includes(currentUserType) && (
        <>
          <Text>
            <Trans
              t={t}
              i18nKey="users:deleteUser.confirmClientAdmin"
              values={{ userName }}
              components={{ b: <b /> }}
            />
          </Text>
          {radioOptions.map(({ value, label }) => (
            <RadioContainer key={label}>
              <RadioInput
                id={label}
                name="notificationPreference"
                label={label}
                value={value}
                onChange={e => setDeletionType(e.target.value)}
                checked={value === deletionType}
              />
            </RadioContainer>
          ))}
        </>
      )}
      <ButtonGroup>
        <Button variant="tertiary" onClick={() => Swal.close()}>
          {t('shared:no')}
        </Button>
        <Button
          variant="containedBlue"
          ml="8px"
          onClick={() => mutate({ userId, deletionType })}
        >
          {t('shared:yes')}
        </Button>
      </ButtonGroup>
    </Wrapper>
  );
}

const Wrapper = styled.section`
  font-family: 'Montserrat', sans-serif;
`;

const Text = styled.p`
  margin: 12px 0 24px;
  font-size: 14px;
  text-align: start;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const RadioContainer = styled.div`
  padding: 8px 0;

  &:first-of-type {
    padding: 0 0 8px;
  }
`;
